import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { setDocumentTitle } from 'shared/util'

import {
	Box, Paper, Stack,
	Tab, Tabs,
	useMediaQuery,
	useTheme
} from '@mui/material'

import { useSideMenu } from 'components/sideMenu/container'
import { useTopBar } from 'components/topBar/container'
import { BreadCrumbItem } from 'components/topBar/types'

import { SearchDetailResult } from 'interface/search'

import { hiddenFlags } from 'features/search/util'
import { useTenant } from 'features/tenant/context'

import { useGrid } from 'components/grid'
import { GridBar } from './components/gridBar'

import { InfoPage } from './pages/info'
import { SisetuPage } from './pages/sisetu'
import { KojinPage } from './pages/kojin'
import { MixedPage } from './pages/mixed'
import { ArticlePage } from './pages/article'
import { TrialPage } from './pages/trial'
import { ResearchPage } from './pages/research'
import { AuthorGraphPage } from './pages/authorGraph'

type GridPageType = 'info' | 'sisetu' | 'kojin' | 'mixed' | 'article' | 'trial' | 'research' | 'graph'
type SearchGridPageProps = {
	search: SearchDetailResult
	topBarState: ReturnType<typeof useTopBar>
	sideMenuState: ReturnType<typeof useSideMenu>
}
export const SearchGridPage = ({
	topBarState: { setBreadCrumbs },
	sideMenuState: { setActivePage },
	search,
	...others
}: SearchGridPageProps) => {
  const theme = useTheme()
  const isSmallMedia = useMediaQuery(theme.breakpoints.down("sm"))

	const tenant = useTenant()
	const [params, setParams] = useSearchParams()
	const [page, setPage] = useState<GridPageType>(
		() => {
			const defaultPage = params.get('page')
			return (
				(defaultPage === 'info') ? 'info' :
				(defaultPage === 'sisetu') ? 'sisetu' :
				(defaultPage === 'kojin') ? 'kojin' :
				(defaultPage === 'mixed') ? 'mixed' :
				(defaultPage === 'article') ? 'article' :
				(defaultPage === 'trial') ? 'trial' :
				(defaultPage === 'research') ? 'research' :
				(defaultPage === 'graph') ? 'graph' : 'info'
			)
		}
	)
	const gridState = useGrid()

	const {
		hideJournal,
		hideTrial,
		hideResearch
	} = useMemo(
		() => hiddenFlags(search),
		[search]
	)
	useEffect(
		() => {
			setParams((prev) => {
				prev.set('page', page)
				return prev
			})
		},
		[page, setParams]
	)
	useEffect(
		() => {
			const crumbs: BreadCrumbItem[] = []
			if (search.manufacturer?.manufacturerName) {
				crumbs.push({
					label: search.manufacturer.manufacturerName,
					url: `/${tenant.domain}/search/list#manufacturer-${search.manufacturer.manufacturerId}`
				})
			}
			crumbs.push({
				label: search.title,
				url: `/${tenant.domain}/search/${search.searchId}/home`
			})
			const pageName = (
				(page === 'info') ? '情報' :
				(page === 'sisetu')  ? '施設' :
				(page === 'kojin')  ? 'KOL' :
				(page === 'mixed')  ? '施設×KOL' :
				(page === 'article')  ? '論文' :
				(page === 'trial')  ? '治験' : 
				(page === 'research')  ? '研究' : 
				(page === 'graph')  ? '共著マップ' : undefined
			)
			if (pageName) {
				crumbs.push({ label: pageName })
			}
			setDocumentTitle(pageName, search.title, tenant.tenantName)
			setBreadCrumbs(crumbs)
			setActivePage('grid')
		},
		[tenant, page, setBreadCrumbs, setActivePage, search]
	)
	return (
		<Stack
			direction="column"
			sx={{
				position: 'relative',
				overflow: 'hidden',
			}}
			{...others}
		>
			<Paper square sx={{ width: '100%' }}>
				<Stack direction="row" spacing={1} alignItems={'flex-end'}>
					<Tabs
						orientation="horizontal"
						variant="scrollable"
						value={page}
						onChange={(ev, value) => {
							ev.preventDefault()
							setPage(value)
						}}
					>
						<Tab value='info' component={Link} to="?page=info" label="情報"/>
						<Tab value='sisetu' component={Link} to="?page=sisetu" label="施設"/>
						<Tab value='kojin' component={Link} to="?page=kojin" label="KOL"/>
						<Tab value='mixed' component={Link} to="?page=mixed" label="施設×KOL"/>
						{hideJournal === false && <Tab value='article' component={Link} to="?page=article" label="論文"/>}
						{hideTrial === false && <Tab value='trial' component={Link} to="?page=trial" label="治験"/>}
						{hideResearch === false && <Tab value='research' component={Link} to="?page=research" label="研究"/>}
						{hideJournal === false && <Tab value='graph' component={Link} to="?page=graph" label="共著マップ"/>}
					</Tabs>
					
					<Box flexGrow={1}/>
					{(
						page === 'sisetu' ? <GridBar {...gridState}/> :
						page === 'kojin' ? <GridBar {...gridState}/> :
						page === 'mixed' ? <GridBar {...gridState}/> :
						page === 'article' ? <GridBar {...gridState}/> :
						page === 'trial' ? <GridBar {...gridState}/> :
						page === 'research' ? <GridBar {...gridState}/> :
						<></>
					)}
				</Stack>
			</Paper>

			<Box
				flexGrow={1}
				boxSizing="border-box"
				width="100%"
				overflow="hidden"
				position="relative"
				padding={isSmallMedia ? 0 : 1}
			>
				{(
					page === 'info' ? <InfoPage search={search} {...gridState}/> :
					page === 'sisetu' ? <SisetuPage search={search} {...gridState}/> :
					page === 'kojin' ? <KojinPage search={search} {...gridState}/> :
					page === 'mixed' ? <MixedPage search={search} {...gridState}/> :
					page === 'article' ? <ArticlePage search={search} {...gridState}/> :
					page === 'trial' ? <TrialPage search={search} {...gridState}/> :
					page === 'research' ? <ResearchPage search={search} {...gridState}/> :
					page === 'graph' ? <AuthorGraphPage search={search}/> :
					<></>
				)}
			</Box>
		</Stack>
	)
}
